import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

const CONTROL =
  'w-full min-h-11 rounded-xl border border-line bg-surface px-4 py-2.5 text-base text-parchment placeholder:text-faint transition-colors duration-200 focus:border-gold focus:outline-none sm:text-sm'

function Label({
  htmlFor,
  children,
  hint,
}: {
  htmlFor: string
  children: ReactNode
  hint?: string
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 flex items-baseline gap-2 text-xs uppercase tracking-[0.14em] text-muted"
    >
      {children}
      {hint ? <span className="lowercase tracking-normal text-faint">({hint})</span> : null}
    </label>
  )
}

export function TextField({
  label,
  hint,
  className,
  ...props
}: ComponentPropsWithoutRef<'input'> & { label: string; hint?: string }) {
  const id = useId()
  return (
    <div className={className}>
      <Label htmlFor={id} hint={hint}>
        {label}
      </Label>
      {/* `text-base` on mobile keeps iOS Safari from zooming on focus. */}
      <input id={id} className={CONTROL} {...props} />
    </div>
  )
}

export function TextAreaField({
  label,
  hint,
  className,
  ...props
}: ComponentPropsWithoutRef<'textarea'> & { label: string; hint?: string }) {
  const id = useId()
  return (
    <div className={className}>
      <Label htmlFor={id} hint={hint}>
        {label}
      </Label>
      <textarea id={id} rows={4} className={cn(CONTROL, 'resize-y')} {...props} />
    </div>
  )
}

export function SelectField({
  label,
  hint,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'select'> & { label: string; hint?: string }) {
  const id = useId()
  return (
    <div className={className}>
      <Label htmlFor={id} hint={hint}>
        {label}
      </Label>
      <div className="relative">
        <select id={id} className={cn(CONTROL, 'appearance-none pr-10')} {...props}>
          {children}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 12 8"
          className="pointer-events-none absolute right-4 top-1/2 h-2 w-3 -translate-y-1/2 fill-none stroke-gold stroke-[1.5]"
        >
          <path d="M1 1.5 6 6.5 11 1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  )
}
